import React from "react";
import { useState } from "react";
import type { ComponentType } from "react";
import {
  ArrowRight,
  Boxes,
  Cable,
  CloudCog,
  Database,
  FileInput,
  FileOutput,
  Radio,
  SearchCheck,
  ShieldCheck,
} from "./icons";
import type { IconProps } from "./icons";
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  Select,
  SelectItem,
  cx,
} from "./ui";

type IconComp = ComponentType<IconProps>;
type Hub = "import" | "export";
type Integration = {
  name: string;
  description: string;
  status: "Connected" | "Available" | "Coming Soon";
  icon: IconComp;
  featured?: boolean;
};

const BASE: Integration[] = [
  { name: "Cargowise", description: "Bidirectional product, entry, shipment, and compliance sync.", status: "Connected", icon: Boxes, featured: true },
  { name: "ERP", description: "Bring product master and supplier data into BITE.", status: "Available", icon: Database },
  { name: "API / Webhook", description: "Automate inbound data and downstream compliance events.", status: "Available", icon: Cable },
  { name: "SFTP / File Drop", description: "Schedule secure file-based product and entry ingestion.", status: "Coming Soon", icon: FileInput },
];

export const IntegrationsPanel: React.FC<{ hub: Hub }> = ({ hub }) => {
  const [selected, setSelected] = useState<Integration | null>(null);
  const integrations: Integration[] =
    hub === "export"
      ? [
          ...BASE,
          { name: "Export Management System", description: "Send vetted product, screening, and authorization packages downstream.", status: "Available", icon: FileOutput },
          { name: "License Management System", description: "Push license details, conditions, entities, and supporting documents.", status: "Coming Soon", icon: ShieldCheck },
        ]
      : [
          { name: "ACE Analyzer", description: "Match ES-003 lines to catalog products without relying on HTS codes, then attach findings to Product 360.", status: "Available", icon: SearchCheck, featured: true },
          ...BASE,
        ];

  return (
    <section className="ih-col ih-gap-4">
      <div className="ih-row-wrap ih-between ih-gap-3">
        <div>
          <div className="ih-row ih-gap-2">
            <h2 className="ih-section-title" style={{ fontSize: 16 }}>Integration ecosystem</h2>
            <PreviewPill />
          </div>
          <p className="ih-text-xs ih-text-muted ih-mt-1" style={{ maxWidth: 640 }}>
            BITE is the compliance layer between source systems and filing or management destinations—bringing data in, enriching it, and handing decisions back downstream.
          </p>
        </div>
      </div>
      <div className="ih-integrations-grid">
        {integrations.map((item) => {
          const Icon = item.icon;
          return (
            <article
              key={item.name}
              className={cx("ih-card", "ih-integration-card", item.featured && "is-featured")}
            >
              <div className="ih-row ih-between ih-gap-3" style={{ alignItems: "flex-start" }}>
                <div className="ih-integration-icon"><Icon size={20} /></div>
                <StatusPill status={item.status} />
              </div>
              <div className="ih-grow">
                <h3 className="ih-font-semibold">{item.name}</h3>
                <p className="ih-text-xs ih-text-muted ih-mt-1">{item.description}</p>
              </div>
              {item.featured && <FlowStrip />}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelected(item)}
                style={{ alignSelf: "flex-start" }}
              >
                {item.status === "Connected" ? "Configure" : "Connect"}
              </Button>
            </article>
          );
        })}
      </div>
      <IntegrationDialog item={selected} onClose={() => setSelected(null)} hub={hub} />
    </section>
  );
}

const FlowStrip: React.FC = () => {
  return (
    <div className="ih-subsurface ih-row ih-between ih-gap-2 ih-text-xs">
      <span className="ih-row ih-gap-1"><FileInput size={14} /> Cargowise / ERP</span>
      <ArrowRight size={14} className="ih-text-muted" />
      <span className="ih-row ih-gap-1 ih-font-semibold ih-tone-info">
        <ShieldCheck size={14} /> BITE compliance
      </span>
      <ArrowRight size={14} className="ih-text-muted" />
      <span className="ih-row ih-gap-1"><FileOutput size={14} /> Filing / management</span>
    </div>
  );
}

const IntegrationDialog: React.FC<{
  item: Integration | null;
  onClose: () => void;
  hub: Hub;
}> = ({
  item,
  onClose,
  hub,
}) => {
  if (!item) return null;
  const mappings: Array<[string, string]> =
    hub === "import"
      ? [
          ["ProductCode", "SKU"],
          ["GoodsDescription", "Product description"],
          ["TariffCode", "HTS code"],
          ["OriginCountry", "Country of origin"],
          ["EntryNumber", "Entry package"],
        ]
      : [
          ["ProductCode", "SKU"],
          ["GoodsDescription", "Product description"],
          ["ExportCode", "Schedule B"],
          ["ControlCode", "ECCN / USML"],
          ["Destination", "Screening destinations"],
        ];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="lg" onClose={onClose}>
        <DialogHeader>
          <DialogTitle>
            <span className="ih-row ih-gap-2">
              {item.name} connection <PreviewPill />
            </span>
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          {item.featured ? (
            <div className="ih-col ih-gap-3">
              <FlowStrip />
              <div className="ih-integration-two-col">
                <div className="ih-subsurface">
                  <FileInput size={16} className="ih-tone-info" />
                  <strong className="ih-mt-2 ih-block">Pull into BITE</strong>
                  <p className="ih-text-xs ih-text-muted ih-mt-1">
                    Products, {hub === "import" ? "entries, shipments, and documents" : "orders, destinations, and parties"}.
                  </p>
                </div>
                <div className="ih-subsurface">
                  <FileOutput size={16} className="ih-tone-info" />
                  <strong className="ih-mt-2 ih-block">Push downstream</strong>
                  <p className="ih-text-xs ih-text-muted ih-mt-1">
                    Classifications, compliance status, flags, and review outcomes.
                  </p>
                </div>
              </div>
              <div>
                <div className="ih-kicker">Field mapping preview</div>
                <div className="ih-col ih-gap-2 ih-mt-2">
                  {mappings.map(([source, target]) => (
                    <div key={source} className="ih-mapping-row">
                      <div className="ih-mono ih-mapping-source">{source}</div>
                      <ArrowRight size={14} className="ih-text-muted" />
                      <Select defaultValue={target}>
                        <SelectItem value={target}>{target}</SelectItem>
                        <SelectItem value="ignore">Do not import</SelectItem>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="ih-config-placeholder">
              <CloudCog size={40} className="ih-text-muted" />
              <h3 className="ih-font-semibold ih-mt-3">Configuration preview</h3>
              <p className="ih-text-xs ih-text-muted ih-mt-1">
                Authentication, sync direction, field mapping, and event settings will live here.
              </p>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
          <Button variant="primary" disabled>
            {item.status === "Connected" ? "Save configuration" : "Connect"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const NEWS: Record<Hub, ReadonlyArray<readonly [string, string, string, string, string]>> = {
  import: [
    ["Jun 10, 2026", "USTR", "Section 301 product exclusion process updated", "New filing windows and eligibility rules may change duty exposure.", "12"],
    ["Jun 7, 2026", "CBP", "UFLPA enforcement guidance expanded", "Additional supply-chain evidence is recommended for textile entries.", "7"],
    ["Jun 3, 2026", "Federal Register", "Steel derivative reporting amendments", "Entry reporting requirements expand for selected Chapter 73 products.", "5"],
  ],
  export: [
    ["Jun 11, 2026", "BIS", "Commerce Control List technical correction", "Selected ECCNs receive updated technical notes and license requirements.", "9"],
    ["Jun 8, 2026", "OFAC", "Sanctions designations updated", "New entities and vessels were added to sanctions programs.", "14"],
    ["Jun 2, 2026", "DDTC", "ITAR registration and filing notice", "Updated submission guidance applies to manufacturers and exporters.", "3"],
  ],
};

export const RegulationsPanel: React.FC<{ hub: Hub }> = ({ hub }) => {
  return (
    <section className="ih-col ih-gap-3">
      <div>
        <div className="ih-row ih-gap-2">
          <h2 className="ih-section-title" style={{ fontSize: 16 }}>
            Regulations & awareness
          </h2>
          <PreviewPill />
        </div>
        <p className="ih-text-xs ih-text-muted ih-mt-1">
          Current regulatory developments matched against your product portfolio.
        </p>
      </div>
      <div className="ih-col ih-gap-2">
        {NEWS[hub].map(([date, source, title, summary, count]) => (
          <article key={title} className="ih-card ih-news-item">
            <div className="ih-news-icon"><Radio size={16} /></div>
            <div className="ih-grow">
              <div className="ih-row-wrap ih-gap-2">
                <span className="ih-text-xs ih-text-muted ih-num">{date}</span>
                <span className="ih-badge ih-badge-info">{source}</span>
              </div>
              <h3 className="ih-font-semibold ih-mt-1">{title}</h3>
              <p className="ih-text-xs ih-text-muted">{summary}</p>
            </div>
            <span className="ih-badge ih-badge-warning" style={{ alignSelf: "flex-start" }}>
              Affects {count} products
            </span>
          </article>
        ))}
      </div>
    </section>
  );
}

const PreviewPill: React.FC = () => {
  return <span className="ih-badge ih-badge-info">Preview</span>;
}

const StatusPill: React.FC<{ status: Integration["status"] }> = ({ status }) => {
  return (
    <span
      className={cx(
        "ih-badge",
        status === "Connected" ? "ih-badge-success" : status === "Available" ? "ih-badge-info" : "ih-badge-warning",
      )}
    >
      {status}
    </span>
  );
}
